import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ThinScroll from "./ThinScroll";

// Styled replacement for a native <select>, used across the dashboard so every
// dropdown reads the same. We avoid <select> because (1) its closed width stretches
// to the widest option — long labels blow out filter rows — and (2) its popup is
// browser-rendered, so it can't take our ThinScroll bar.
//
// The popover is rendered in a PORTAL with fixed positioning so it escapes any
// ancestor `overflow-hidden` (the analysis-panel cards clip otherwise) and is never
// cut off. The menu sizes to the WIDEST option (`w-max`), is at least as wide as the
// trigger, and its height is capped to the space below the trigger so long lists
// scroll instead of overflowing the viewport.
//
// Controlled: pass `value` and `onChange(value)`. `options` is `[{ value, label }]`.
// `value` is matched against option values with `===`, so numeric values round-trip
// as numbers (callers don't need to coerce in onChange like with e.target.value).
const SelectMenu = ({
  options = [],
  value,
  onChange,
  disabled = false,
  placeholder = "Select…",
  ariaLabel,
  className = "",
  menuClassName = "",
  // When true, the trigger reserves the width of the WIDEST option so it never
  // resizes as the selection changes. Best when option labels are similar length
  // (e.g. the date picker). Leave false for "All"-style filters where the default
  // is much shorter than the options, or the trigger renders as a wide, mostly-empty
  // box with the chevron pushed far right.
  stableWidth = false,
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Position the portal menu under the trigger, capping height to the space below.
  const place = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      top: r.bottom + 4,
      left: r.left,
      minWidth: r.width,
      maxHeight: Math.max(160, window.innerHeight - r.bottom - 12),
    });
  };

  useLayoutEffect(() => {
    if (open) place();
  }, [open]);

  // Set on the portal menu: stash the node for outside-click checks and, once we know
  // its rendered width (which depends on the longest option), shift it left if it
  // would spill past the right edge of the viewport.
  const setMenuNode = (node) => {
    menuRef.current = node;
    if (!node) return;
    const w = node.offsetWidth;
    setPos((p) => {
      if (!p) return p;
      const overflow = p.left + w - (window.innerWidth - 8);
      return overflow > 0 ? { ...p, left: Math.max(8, p.left - overflow) } : p;
    });
  };

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onReflow = () => place();
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const triggerLabel = selected ? selected.label : placeholder;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className={`inline-flex items-center justify-between gap-2 w-auto max-w-full text-left text-sm rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#32B1FF] disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
      >
        {stableWidth ? (
          // Width sizer: every option label is stacked in the same grid cell, so the
          // trigger reserves the width of the WIDEST option and doesn't jump around as
          // the selection changes. Only the last (visible) span is shown; the rest are
          // invisible + zero-height so they add width but no height.
          <span className="grid min-w-0">
            {options.map((o) => (
              <span
                key={`sizer-${String(o.value)}`}
                aria-hidden="true"
                className="col-start-1 row-start-1 h-0 overflow-hidden whitespace-nowrap invisible"
              >
                {o.label}
              </span>
            ))}
            <span className="col-start-1 row-start-1 truncate">{triggerLabel}</span>
          </span>
        ) : (
          <span className="truncate min-w-0">{triggerLabel}</span>
        )}
        <svg
          className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open &&
        pos &&
        createPortal(
          <ThinScroll
            ref={setMenuNode}
            role="listbox"
            style={{ position: "fixed", top: pos.top, left: pos.left, minWidth: pos.minWidth, maxHeight: pos.maxHeight }}
            className={`z-[60] w-max max-w-[calc(100vw-1rem)] overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg py-1 ${menuClassName}`}
          >
            {options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">{placeholder}</div>
            ) : (
              options.map((o) => {
                const active = o.value === value;
                return (
                  <button
                    key={String(o.value)}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => {
                      onChange(o.value);
                      setOpen(false);
                    }}
                    className={`block w-full text-left px-3 py-1.5 text-sm whitespace-nowrap transition-colors ${
                      active
                        ? "bg-[#32B1FF]/10 text-[#32B1FF] font-medium"
                        : "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                    }`}
                  >
                    {o.label}
                  </button>
                );
              })
            )}
          </ThinScroll>,
          document.body
        )}
    </>
  );
};

export default SelectMenu;
